import type { KeyDownEvent, RowDblClickEvent } from 'devextreme/ui/data_grid';
import type { DataGridRef } from 'devextreme-react/data-grid';
import type { RefObject } from 'react';

/** Shared DevExtreme DataGrid props for full-bleed site page grids. */
export const OPS_SITE_GRID_PROPS = {
  rowAlternationEnabled: true,
  hoverStateEnabled: true,
  focusStateEnabled: true,
  focusedRowEnabled: true,
  keyboardNavigation: {
    enabled: true
  },
  wordWrapEnabled: false
} as const;

/** Open detail (or other row action) when Enter is pressed on the focused data row. */
export function onGridFocusedRowEnter<T>(
  e: KeyDownEvent<T, string | number>,
  handler: (row: T) => void
): void {
  if (e.handled) return;
  const event = e.event;
  if (!event || event.key !== 'Enter') return;

  const target = event.target as HTMLElement | null;
  if (
    target?.closest(
      '.dx-texteditor-input, .dx-dropdowneditor-input-wrapper, .dx-numberbox, .dx-selectbox, .dx-checkbox'
    )
  ) {
    return;
  }

  const grid = e.component;
  const focusedRowIndex = grid.option('focusedRowIndex');
  if (focusedRowIndex == null || focusedRowIndex < 0) return;

  const rowData = grid.getVisibleRows()[focusedRowIndex]?.data as T | undefined;
  if (!rowData) return;

  e.handled = true;
  event.preventDefault();
  handler(rowData);
}

/** Clears persisted grid layout and restores default column state. */
export function resetOpsGridLayout(storageKey: string, gridRef: RefObject<DataGridRef | null>) {
  localStorage.removeItem(storageKey);
  gridRef.current?.instance()?.state(null);
}

/** Stop event propagation and invoke handler when row data is present. */
export function onGridRowDblClick<T>(
  e: RowDblClickEvent<T, string | number>,
  handler: (row: T) => void
): void {
  e.event?.stopPropagation();
  if (e.data) handler(e.data);
}
